import { createFileRoute, useRouter, useNavigate } from '@tanstack/react-router';
import { Show, useUser } from '@clerk/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQuery } from 'convex/react';
import { api } from '@/lib/api';
import { ProfileTabContainer, SUBSTITUTE_TABS, PROVIDER_TABS } from '@/components/features/profile/ProfileTabContainer';
import type { ProfileTab } from '@/components/features/profile/ProfileTabContainer';
import { ProfileGeneralTab } from '@/components/features/profile/tabs/profile-general-tab';
import { ProfileDocumentsTab } from '@/components/features/profile/tabs/ProfileDocumentsTab';
import { ProfileServiceAreasTab } from '@/components/features/profile/tabs/ProfileServiceAreasTab';
import { ProfileNotificationsTab } from '@/components/features/profile/tabs/profile-notifications-tab';
import { ProfilePaymentTab } from '@/components/features/profile/tabs/ProfilePaymentTab';
import { ProfileSecurityTab } from '@/components/features/profile/tabs/profile-security-tab';
import { ProfileBusinessLocationsTab } from '@/components/features/profile/tabs/ProfileBusinessLocationsTab';
import { ProfileCompletionBanner } from '@/components/features/profile-completion-banner';
import { getUserRoleFromCurrentUser } from '@/hooks/use-current-user-role';

// Search params validation
type ProfileSearch = {
  tab?: string;
};

const ProfilePage = () => {
  const { t } = useTranslation('common');
  const { isLoaded } = useUser();
  const { tab } = Route.useSearch();
  const router = useRouter();
  const navigate = useNavigate();
  const currentUser = useQuery(api.template.users.getCurrentUser);
  const userProfile = useQuery(api.app.users.queries.getMyUserProfile);

  const [transReady, setTransReady] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    void import('@/i18n').then(async (mod) => {
      const i18nInst = mod.default;
      try {
        if (!i18nInst.hasResourceBundle(i18nInst.language, 'common')) {
          await i18nInst.loadNamespaces?.(['common']);
        }
      } catch (e) {
        // ignore
      }
      if (mounted) setTransReady(true);
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch stats based on user role
  const substituteStats = useQuery(api.app.substituteDashboard.getSubstituteStats as any, currentUser && 'role' in currentUser && currentUser.role === 'substitute' ? {} : 'skip') as any;

  const providerDashboardStats = useQuery(api.app.providerDashboard.getProviderDashboardStats as any, currentUser && 'role' in currentUser && currentUser.role === 'provider' ? {} : 'skip') as any;

  const providerMetrics = useQuery(api.app.providerDashboard.getProviderMetrics as any, currentUser && 'role' in currentUser && currentUser.role === 'provider' ? {} : 'skip') as any;

  if (!transReady || !isLoaded) return null;

  const role = getUserRoleFromCurrentUser(currentUser as Parameters<typeof getUserRoleFromCurrentUser>[0]);
  if (role === undefined) {
    return null;
  }
  const isSubstitute = role === 'substitute';

  // Map tabs to components
  const substituteTabsWithComponents: ProfileTab[] = [
    { ...SUBSTITUTE_TABS[0], component: ProfileGeneralTab },
    { ...SUBSTITUTE_TABS[1], component: ProfileDocumentsTab },
    { ...SUBSTITUTE_TABS[2], component: ProfileServiceAreasTab },
    { ...SUBSTITUTE_TABS[3], component: ProfileNotificationsTab },
    { ...SUBSTITUTE_TABS[4], component: ProfilePaymentTab },
    { ...SUBSTITUTE_TABS[5], component: ProfileSecurityTab },
  ];

  const providerTabsWithComponents: ProfileTab[] = [
    { ...PROVIDER_TABS[0], component: ProfileGeneralTab },
    { ...PROVIDER_TABS[1], component: ProfileDocumentsTab },
    { ...PROVIDER_TABS[2], component: ProfileBusinessLocationsTab },
    { ...PROVIDER_TABS[3], component: ProfileNotificationsTab },
    { ...PROVIDER_TABS[4], component: ProfileSecurityTab },
  ];

  const tabs = isSubstitute ? substituteTabsWithComponents : providerTabsWithComponents;

  // Determine active tab (default to 'general' if invalid or not specified)
  const validTabIds = tabs.map((t) => t.id);
  const activeTab = tab && validTabIds.includes(tab) ? tab : 'general';

  // Prepare stats for ProfileSidebar
  const stats = isSubstitute
    ? {
        rating: userProfile?.rating,
        applications: substituteStats?.activeApplications,
        services: substituteStats?.completedDuties,
      }
    : {
        rating: userProfile?.rating,
        applications: providerDashboardStats?.pendingApplications,
        services: providerMetrics?.totalCompletedDuties,
      };

  // Settings is reached both as a primary tab and as a sub-flow (profile banner, "Betriebsstätte
  // erforderlich" gate). Give it an explicit way back: return to the previous screen, or fall back
  // to the role dashboard on a direct load. Mobile-only — desktop has the persistent sidebar.
  const handleBack = (): void => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.history.back();
      return;
    }
    const dashboard = role === 'substitute' ? '/substitute/dashboard' : role === 'operator' || role === 'admin' ? '/operators/dashboard' : '/provider/dashboard';
    void navigate({ to: dashboard });
  };

  return (
    <Show when='signed-in'>
      <div className='p-4 sm:p-6 lg:p-8 space-y-6'>
        <div className='flex items-center gap-1'>
          <Button variant='ghost' size='icon' onClick={handleBack} aria-label={t('actions.back', 'Zurück')} className='md:hidden -ml-2 size-11'>
            <ArrowLeft className='size-5' />
          </Button>
          <h1 className='text-2xl sm:text-3xl font-bold text-foreground tracking-tight'>{t('userProfile.title')}</h1>
        </div>

        {/* DA-438: Profile Completion Banner — CTA hidden here; this page IS where the profile is completed */}
        <ProfileCompletionBanner showCta={false} />

        {currentUser && '_id' in currentUser && <ProfileTabContainer tabs={tabs} activeTab={activeTab} user={currentUser} profile={userProfile || undefined} stats={stats} />}
      </div>
    </Show>
  );
};

export const Route = createFileRoute('/_protected/profile')({
  validateSearch: (search: Record<string, unknown>): ProfileSearch => {
    const tab = search['tab'] as string | undefined;
    return tab !== undefined ? { tab } : {};
  },
  loader: async () => {
    const { default: i18nMod } = await import('../i18n');
    await i18nMod.loadNamespaces?.(['nav', 'common']);
    return null;
  },
  component: ProfilePage,
});
